import { Button, Icon, Modal } from "@legal-500/legal500-design-system";
import styles from "./Modal.module.css";

export type RetryAllPhase = "confirm" | "progress" | "done" | "error";

type RetryAllModalProps = {
  phase: RetryAllPhase;
  total: number;
  enqueued: number;
  skipped: number;
  errorMessage?: string | null;
  errorStatus?: number | null;
  onConfirm: () => void;
  onClose: () => void;
};

export function RetryAllModal({
  phase,
  total,
  enqueued,
  skipped,
  errorMessage,
  errorStatus,
  onConfirm,
  onClose,
}: RetryAllModalProps) {
  const isRunning = phase === "progress";
  const skippedSuffix = skipped > 0 ? `, skipped ${skipped}` : "";

  // Retry-all is a single fire-and-forget request with no cancel, so while it
  // is in flight we swallow close requests (backdrop + the X button); the
  // explicit Close button in the terminal phases drives termination instead.
  const noopClose = (): void => {
    // intentionally empty
  };

  return (
    <Modal
      isOpen={true}
      onClose={isRunning ? noopClose : onClose}
      title="Retry all matching jobs"
      size="small"
      blurBackground
      backdropClick={false}
    >
      <div className={styles.bulkParse}>
        {phase === "confirm" && (
          <>
            <p className={styles.bulkParseMessage}>
              Re-enqueue all {total} job(s) matching the current filters?
            </p>
            <div className={styles.modalFooter}>
              <Button onClick={onClose} variant="outlined" color="default" aria-label="Cancel">
                Cancel
              </Button>
              <Button onClick={onConfirm} variant="contained" color="default" aria-label="Confirm">
                Confirm
              </Button>
            </div>
          </>
        )}

        {phase === "progress" && (
          <>
            <p className={styles.bulkParseMessage}>Enqueueing {total} job(s)…</p>
            <p className={styles.bulkParseProgressText}>
              Large batches can take a while — keep this tab open.
            </p>
          </>
        )}

        {phase === "done" && (
          <>
            <div className={styles.bulkParseSummary}>
              <Icon icon="check" size="small" className={styles.successIcon} />
              <span>
                Queued {enqueued} job(s){skippedSuffix}
              </span>
            </div>
            <div className={styles.modalFooter}>
              <Button onClick={onClose} variant="contained" color="default" aria-label="Close">
                Close
              </Button>
            </div>
          </>
        )}

        {phase === "error" && (
          <>
            <div className={styles.formError}>
              <Icon icon="exclamation" size="small" />
              <span>{errorMessage}</span>
            </div>
            {/* 408: the HTTP layer timed out but the server keeps enqueueing in
                the background — warn before the user retries and double-queues.
                (409's server message already explains itself, no extra hint.) */}
            {errorStatus === 408 && (
              <p className={styles.bulkParseMessage}>
                The server may still be processing this retry in the background — refresh the list
                in a minute before retrying.
              </p>
            )}
            <div className={styles.modalFooter}>
              <Button onClick={onClose} variant="contained" color="default" aria-label="Close">
                Close
              </Button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}
